<template>
	<div :class="cardClasses" :style="cardStyles">
		<header
			:class="bemm('header')"
			v-if="$slots.header || $slots.actions || title || collapsible"
		>
			<div
				:class="bemm('header-container', { clickable: collapsible })"
				v-if="$slots.header || title"
				@click="handleTitleClick"
			>
				<slot name="header">
					<div :class="bemm('header-title')">
						<Icon v-if="icon" :name="icon" :class="bemm('icon')"></Icon>
						<h3 v-if="title" :class="bemm('title')">{{ title }}</h3>
					</div>
					<h4 v-if="subTitle" :class="bemm('category')">{{ subTitle }}</h4>
					<p v-if="description" :class="bemm('description')">
						{{ description }}
					</p>
				</slot>
			</div>

			<div :class="bemm('actions')" v-if="$slots.actions || collapsible">
				<slot name="actions"></slot>
				<button
					v-if="collapsible"
					type="button"
					:class="bemm('collapse-toggle')"
					:aria-expanded="isOpen"
					@click="toggle"
				>
					<Icon :name="isOpen ? Icons.CHEVRON_UP : Icons.CHEVRON_DOWN"></Icon>
				</button>
			</div>
		</header>

		<div
			v-if="$slots.default && (!collapsible || isOpen)"
			:class="bemm('body')"
		>
			<slot></slot>
		</div>

		<slot name="raw-content"></slot>

		<footer
			:class="bemm('footer')"
			v-if="$slots.footer || hasFooterMetadata || $slots['footer-actions']"
		>
			<slot name="footer">
				<div :class="bemm('footer-layout')">
					<div
						v-if="hasFooterMetadata"
						:class="bemm('metadata')"
						:style="metadataStyles"
					>
						<DL
							v-for="item in visibleMetadata"
							:key="item.label"
							:class="bemm('metadata-item')"
							:label="item.label"
							:value="item.value"
						/>
					</div>

					<div v-if="$slots['footer-actions']" :class="bemm('footer-actions')">
						<slot name="footer-actions"></slot>
					</div>
				</div>
			</slot>
		</footer>
	</div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, watch } from 'vue';
import { CardType, type BaseCardProps } from './Card.model';
import { useBemm } from 'bemm';
import { Color, Size } from '@arritech/shared/types';
import { Icon, Icons } from '../Icon';
import DL from '../Display/DL.vue';

const props = defineProps({
	type: {
		type: String as PropType<BaseCardProps['type']>,
		default: CardType.DEFAULT,
	},
	color: {
		type: String as PropType<BaseCardProps['color']>,
		default: Color.BACKGROUND,
	},
	themeColor: {
		type: String as PropType<BaseCardProps['themeColor']>,
		default: Color.SECONDARY,
	},
	title: {
		type: String as PropType<BaseCardProps['title']>,
		default: '',
	},
	subTitle: {
		type: String as PropType<BaseCardProps['subTitle']>,
		default: '',
	},
	description: {
		type: String as PropType<BaseCardProps['description']>,
		default: '',
	},
	category: {
		type: String as PropType<BaseCardProps['category']>,
		default: '',
	},
	sticky: {
		type: Boolean as PropType<BaseCardProps['sticky']>,
		default: false,
	},
	stickyHeader: {
		type: Boolean as PropType<BaseCardProps['stickyHeader']>,
		default: false,
	},
	size: {
		type: String as PropType<BaseCardProps['size']>,
		default: Size.LARGE,
	},
	icon: {
		type: String as PropType<BaseCardProps['icon']>,
		default: null,
	},
	metadata: {
		type: Array as PropType<BaseCardProps['metadata']>,
		default: () => [],
	},
	collapsible: {
		type: Boolean as PropType<BaseCardProps['collapsible']>,
		default: false,
	},
	defaultOpen: {
		type: Boolean as PropType<BaseCardProps['defaultOpen']>,
		default: true,
	},
	modelValue: {
		type: Boolean as PropType<boolean | undefined>,
		default: undefined,
	},
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void;
}>();

const bemm = useBemm('ar-card', {
	return: 'string',
	includeBaseClass: true,
});

const isOpen = ref(props.modelValue ?? props.defaultOpen);

watch(
	() => props.modelValue,
	(value) => {
		if (value !== undefined) {
			isOpen.value = value;
		}
	}
);

const toggle = () => {
	if (!props.collapsible) return;
	isOpen.value = !isOpen.value;
	emit('update:modelValue', isOpen.value);
};

const handleTitleClick = () => {
	if (!props.collapsible) return;
	toggle();
};

const cardClasses = computed(() =>
	bemm('', [
		props.type,
		props.size,
		props.sticky ? 'sticky' : '',
		props.stickyHeader ? 'sticky-header' : '',
		props.collapsible ? 'collapsible' : '',
		props.collapsible ? (isOpen.value ? 'open' : 'closed') : '',
	])
);

const cardStyles = computed((): Record<string, string> => {
	return props.color
		? {
				'--card-color': `var(--color-${props.color})`,
				'--card-text-color': `var(--color-${props.color}-contrast)`,
				'--color-theme': props.themeColor
					? `var(--color-${props.themeColor})`
					: undefined,
				'--color-theme-contrast': props.themeColor
					? `var(--color-${props.themeColor}-contrast)`
					: undefined,
			}
		: {};
});

const visibleMetadata = computed(() =>
	(props.metadata || [])
		.filter(
			(item) =>
				item &&
				item.label &&
				item.value !== null &&
				item.value !== undefined &&
				item.value !== ''
		)
		.map((item) => ({
			label: item.label,
			value: typeof item.value === 'boolean' ? String(item.value) : item.value,
		}))
);

const hasFooterMetadata = computed(() => visibleMetadata.value.length > 0);

const metadataColumnCount = computed(() => {
	const count = visibleMetadata.value.length;
	if (count <= 3) return count;
	return Math.ceil(Math.sqrt(count));
});

const metadataStyles = computed(
	(): Record<string, string> => ({
		'--card-metadata-columns': String(metadataColumnCount.value),
	})
);
</script>

<style lang="scss">
.ar-card {
	$b: &;

	--int-border-width: 1px;
	--int-card-border-color: var(
		--card-border-color,
		var(--card-color, color-mix(in srgb, currentColor, transparent 80%))
	);

	--int-card-background-color: color-mix(
		in srgb,
		var(--int-card-color, var(--card-color)),
		var(--color-background) 95%
	);

	border-radius: var(--card-border-radius, var(--border-radius-l));
	border: var(
		--card-border,
		var(--int-border-width) solid var(--int-card-border-color)
	);
	background: var(--card-background-color, var(--int-card-background-color));
	display: flex;
	flex-direction: column;
	width: 100%;
	margin: 0 auto;
	position: relative;
	container-type: inline-size;

	&:has(:focus) {
		z-index: 5;
	}

	&--raised {
		border-radius: var(--border-radius-xl);

		@keyframes headerOpacity {
			0% {
				opacity: 1;
				transform: scale(1);
			}
			100% {
				opacity: 0;
				transform: scale(0.95);
			}
		}
	}

	&--full-width {
		width: 100%;
		max-width: calc(100vw - (var(--space) * 3));
	}

	// Variants

	&--raised {
		--int-border-width: 1px;
		--int-card-padding: var(--space-l);
		--int-card-title-size: var(--font-size-xl);
		--int-card-header-background-color: color-mix(
			in srgb,
			var(--color-primary),
			transparent 97.5%
		);
		--int-card-footer-background-color: var(--int-card-header-background-color);

		border: none;
		box-shadow: var(--card-shadow, 4px 4px 12px 0px rgba(0, 0, 0, 0.05));

		#{$b}__footer {
			background-color: color-mix(
				in srgb,
				var(--color-primary),
				transparent 97.5%
			);
		}
	}

	@container (max-width: 400px) {
		&--raised {
			--int-card-padding: var(--space);
			--int-card-title-size: var(--font-size-l);
		}
	}

	&--default {
		--int-card-padding: var(--space);
		--int-border-width: 0;
		--int-card-header-background-color: transparent;

		border: none;
		background-color: var(--card-color);
		color: var(--card-text-color, var(--color-foreground));
	}

	&--outline {
		--int-card-padding: var(--space);
		--int-card-header-background-color: transparent;
		--int-card-border-color: color-mix(in srgb, currentColor, transparent 80%);
		--int-card-header-border: 1px solid
			color-mix(in srgb, currentColor, transparent 90%);
	}

	&--ghost {
		--int-card-padding: 0;
		--int-card-header-background-color: transparent;
		--int-card-color: transparent;
		--int-card-background-color: transparent;
		background: transparent;
		border: 0;
		box-shadow: none;
		gap: var(--space);
	}

	&--plain {
		--int-card-padding: var(--space);
		--int-card-background-color: color-mix(
			in srgb,
			var(--card-color),
			var(--color-background) 95%
		);
		--int-card-header-background-color: transparent;
		--int-card-border-color: transparent;
		gap: var(--space);
	}

	// Sizes

	&--small {
		--int-card-title-size: var(--font-size-s);
		&#{$b}--outline {
			--int-card-title-font-weight: var(--font-weight-bold);
			--int-card-border-color_header: transparent;
		}
		--int-card-header-align: center;
		--int-card-header-padding: var(--space) var(--space);
		--int-card-icon-size: 2em;
	}

	&--medium {
		--int-card-title-size: var(--font-size-m);
		--int-card-header-align: center;
		--int-card-header-padding: var(--space) var(--space);
		--int-card-icon-size: 2em;
	}

	&__header {
		padding: var(
			--card-padding,
			var(--int-card-header-padding, var(--int-card-padding, var(--space)))
		);

		display: flex;
		justify-content: var(--card-header-justify, space-between);
		align-items: var(
			--card-header-align,
			var(--int-card-header-align, flex-start)
		);
		gap: var(--space);
		border-radius: var(--border-radius-xl) var(--border-radius-xl) 0 0;
		background-color: var(--int-card-header-background-color);
		border-bottom: var(--int-card-header-border, none);

		width: 100%;
		flex-wrap: wrap;
	}

	&__header-title {
		display: flex;
		align-items: center;
		gap: var(--space);
	}

	&__header-container--clickable {
		cursor: pointer;
	}

	&__collapse-toggle {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 1.8em;
		height: 1.8em;
		border: none;
		border-radius: var(--border-radius-s);
		background: transparent;
		color: color-mix(in srgb, currentColor, transparent 45%);
		cursor: pointer;
		transition:
			background-color 150ms ease,
			color 150ms ease;

		&:hover,
		&:focus-visible {
			background-color: color-mix(in srgb, currentColor, transparent 92%);
			color: currentColor;
		}

		&:focus-visible {
			outline: 2px solid var(--color-secondary);
			outline-offset: 1px;
		}
	}

	&__icon {
		font-size: var(--card-icon-size, var(--int-card-icon-size, 3em));
		color: var(--color-theme, currentColor);
		background-color: color-mix(in srgb, var(--color-theme), transparent 90%);
		padding: var(--space-xs);
		border-radius: var(--border-radius-s);
		opacity: 0.8;
	}

	&--sticky-header > #{$b}__header {
		position: sticky;
		top: 0;
		z-index: 5;
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: var(--space);
		flex-shrink: 0;
		width: fit-content;
	}

	&__body {
		display: flex;
		flex-direction: column;
		gap: var(--space);
		padding: var(--card-padding, var(--int-card-padding, var(--space)));
		height: 100%;
	}

	&__footer {
		padding: var(--card-padding, var(--int-card-padding, var(--space)));

		background-color: var(
			--int-card-footer-background-color,
			color-mix(in srgb, var(--int-card-border-color), transparent 90%)
		);
		color: color-mix(in srgb, currentColor, transparent 33.33%);
		display: flex;
		align-items: center;
		flex-shrink: 0;
		height: var(--card-footer-height, fit-content);
	}

	&__footer-layout {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space);
		width: 100%;
		flex-wrap: wrap;
	}

	&__metadata {
		display: grid;
		grid-template-columns: repeat(
			var(--card-metadata-columns, 1),
			minmax(0, 1fr)
		);
		gap: var(--space-m);
		flex: 1 1 320px;
		min-width: 0;
	}

	@container (max-width: 600px) {
		&__metadata {
			grid-template-columns: repeat(
				auto-fit,
				minmax(var(--card-metadata-min-width, 140px), 1fr)
			);
		}
	}

	&__metadata-item {
		min-width: 0;
	}

	&__footer-actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: var(--space);
		flex: 0 0 auto;
		margin-left: auto;
	}

	&__title {
		font-size: var(
			--card-title-size,
			var(--int-card-title-size, var(--font-size-l))
		);
		text-align: left;
		margin: 0;
		font-weight: var(
			--card-title-font-weight,
			var(--int-card-title-font-weight, 500)
		);

		& + #{$b}__description {
			margin-top: var(--space-s);
		}
	}

	&__header-title {
		& + #{$b}__description {
			opacity: 0.5;
			margin-top: var(--space-s);
		}
	}

	&--sticky {
		position: sticky;
		top: 0;
		z-index: 10;
		height: fit-content;
	}
}
</style>
